import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation
} from 'react-router-dom'
import { Flex, Spinner } from '@chakra-ui/react'
import { HelmetProvider } from 'react-helmet-async'
import Login from './views/Login'
import Root from './views/Root'
import GuardedRoute from './auth/GuardedRoute'
import { AuthProvider } from './auth/AuthProvider'
import UnguardedRoute from './auth/UnguardedRoute'
import Calendar from './views/Calendar'
import Signup from './views/Signup'
import { lazy, Suspense, useEffect } from 'react'
import { trackPageView } from './services/analytics'
import Dashboard from './client/modules/dashboard/scenes/Dashboard'
import AccountFrozen from './client/modules/account/scenes/AccountFrozen'
import { AccountProvider } from './client/modules/account/AccountProvider'
import Settings from './client/modules/settings/scenes/Settings'
import Referrals from './client/modules/referral/scenes/Referrals'
import Onboarding from './client/modules/account/scenes/Onboarding'
import { LocaleShell } from './components/LocaleShell/LocaleShell'
import CreatePatient from './views/CreatePatient'
import { GrowthChartsLanding } from './pages/GrowthChartsLanding/GrowthChartsLanding'
import Landing from './pages/Landing/Landing'
import { RoleProvider } from './providers/RoleProvider'
import SecretaryRoute from './auth/SecretaryRoute'
import SecretaryDashboard from './views/SecretaryDashboard/SecretaryDashboard'
import AuthenticatedRoute from './auth/AuthenticatedRoute'

const ReportsPage = lazy(
  async () => await import('./components/Reports/ReportsPage')
)
const CollectionsPage = lazy(async () => ({
  default: (await import('./components/Reports/ReportsPage')).CollectionsPage
}))
const SalesPlaybook = lazy(
  async () => await import('./pages/SalesPlaybook/SalesPlaybook')
)

const ArabicBlogIndex = lazy(async () => await import('./pages/Blog/BlogIndex'))
const BlogPost01 = lazy(
  async () => await import('./pages/Blog/Post01BestProgram')
)
const BlogPost02 = lazy(
  async () => await import('./pages/Blog/Post02Comparison')
)
const BlogPost03 = lazy(
  async () => await import('./pages/Blog/Post03Appointments')
)
const BlogPost04 = lazy(
  async () => await import('./pages/Blog/Post04PatientFiles')
)
const BlogPost05 = lazy(async () => await import('./pages/Blog/Post05Revenue'))
const BlogPost06 = lazy(
  async () => await import('./pages/Blog/Post06Invoicing')
)
const BlogPost07 = lazy(async () => await import('./pages/Blog/Post07WhatsApp'))
const BlogPost08 = lazy(
  async () => await import('./pages/Blog/Post08Digitization')
)
const BlogPost09 = lazy(
  async () => await import('./pages/Blog/Post09MissedAppointments')
)
const BlogPost10 = lazy(
  async () => await import('./pages/Blog/Post10ChooseSystem')
)

function RouteTracker() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location])
  return null
}

function App() {
  const makeGuardedRoute = (component: JSX.Element) => (
    <GuardedRoute>{component}</GuardedRoute>
  )

  const makeUnguardedRoute = (component: JSX.Element) => (
    <UnguardedRoute>{component}</UnguardedRoute>
  )

  useEffect(() => {
    if (
      !location.hostname.includes('sijilo') &&
      !location.hostname.includes('localhost')
    ) {
      location.replace('https://sijilo.com')
    }
  }, [])

  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <RouteTracker />
          <RoleProvider>
            <AccountProvider>
              <LocaleShell>
                <Suspense
                  fallback={
                    <Flex justify="center" align="center" minH="40vh">
                      <Spinner />
                    </Flex>
                  }
                >
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="sales-playbook" element={<SalesPlaybook />} />
                    <Route path="ar" element={<Navigate to="/" replace />} />
                    <Route path="ar/blog" element={<ArabicBlogIndex />} />
                    <Route
                      path="ar/blog/afdal-barnamaj-idarat-iyada-masr"
                      element={<BlogPost01 />}
                    />
                    <Route
                      path="ar/blog/muqarana-anzima-idarat-iyada-masr"
                      element={<BlogPost02 />}
                    />
                    <Route
                      path="ar/blog/tanzim-mawaid-iyada-taqlil-ghiyab"
                      element={<BlogPost03 />}
                    />
                    <Route
                      path="ar/blog/idarit-malafat-marda-elektroniyyan"
                      element={<BlogPost04 />}
                    />
                    <Route
                      path="ar/blog/ziyada-iradat-iyada-masr"
                      element={<BlogPost05 />}
                    />
                    <Route
                      path="ar/blog/dalil-fawatir-tibbiya-masr"
                      element={<BlogPost06 />}
                    />
                    <Route
                      path="ar/blog/tazkirat-whatsapp-marda-iyada"
                      element={<BlogPost07 />}
                    />
                    <Route
                      path="ar/blog/fawaid-raqmana-iyada-masr"
                      element={<BlogPost08 />}
                    />
                    <Route
                      path="ar/blog/asbab-dayaa-mawaid-iyada"
                      element={<BlogPost09 />}
                    />
                    <Route
                      path="ar/blog/ikhtiyar-barnamaj-idarat-iyada-masr"
                      element={<BlogPost10 />}
                    />
                    <Route
                      path="growth-charts"
                      element={<GrowthChartsLanding />}
                    />
                    <Route
                      path="login"
                      element={makeUnguardedRoute(<Login />)}
                    />
                    <Route
                      path="signup"
                      element={makeUnguardedRoute(<Signup />)}
                    />
                    <Route
                      path="dashboard"
                      element={makeGuardedRoute(<Dashboard />)}
                    />
                    <Route
                      path="analytics"
                      element={makeGuardedRoute(
                        <ReportsPage family="analytics" />
                      )}
                    />
                    <Route
                      path="finances"
                      element={makeGuardedRoute(
                        <ReportsPage family="finance" />
                      )}
                    />
                    <Route
                      path="collections"
                      element={
                        <SecretaryRoute>
                          <CollectionsPage />
                        </SecretaryRoute>
                      }
                    />
                    <Route
                      path="onboarding"
                      element={makeGuardedRoute(<Onboarding />)}
                    />
                    <Route
                      path="/patients/new"
                      element={makeGuardedRoute(<CreatePatient />)}
                    />
                    <Route
                      path="/patients/:patientId"
                      element={makeGuardedRoute(<Root />)}
                    />
                    <Route
                      path="calendar"
                      element={
                        <AuthenticatedRoute>
                          <Calendar />
                        </AuthenticatedRoute>
                      }
                    />
                    <Route
                      path="frozen"
                      element={makeGuardedRoute(<AccountFrozen />)}
                    />
                    <Route
                      path="settings"
                      element={makeGuardedRoute(<Settings />)}
                    />
                    <Route
                      path="referrals"
                      element={makeGuardedRoute(<Referrals />)}
                    />
                    <Route
                      path="secretary"
                      element={
                        <SecretaryRoute>
                          <SecretaryDashboard />
                        </SecretaryRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </LocaleShell>
            </AccountProvider>
          </RoleProvider>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  )
}

export default App
